import type { CSSObject } from '@mui/material/styles'

import { styled } from '../../../utils/styled'

import Grid from '../../Grid'
import typography from '../../theme/typography'
import Accordion from '../../Accordion'
import type { SemanticTokens } from '../../theme/tokens/generated/types'
import { keyframes } from '../../theme'

export const StyledGridMessageCardContainer = styled(Grid)<{ $isCurrentUser?: boolean; $isSystemMessage?: boolean }>(
  ({ $isCurrentUser: isCurrentUser = false, $isSystemMessage: isSystemMessage = false }) => ({
    gap: '8px',
    ...(!isSystemMessage && {
      padding: '0px 8px',
      paddingRight: isCurrentUser ? '0px' : '32px',
      paddingLeft: isCurrentUser ? '32px' : '0px',
      display: 'flex',
      flexDirection: isCurrentUser ? 'row-reverse' : 'row'
    })
  })
)

export const StyledGridMessageAvatarContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-end'
}))

const speechBubbleTailSvg = (fill: string): string =>
  encodeURIComponent(
    `<svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0C0 6.00443 2.6463 11.3907 6.83594 15.0566C7.19723 15.373 6.98123 16 6.50098 16H0V0Z" fill="${fill}"/></svg>`
  )

export const StyledGridMessageCardContent = styled(Grid)<{ $isCurrentUser: boolean; $isInternal?: boolean }>(
  ({ $isCurrentUser: isCurrentUser, theme, $isInternal: isInternal = false }) => {
    let messageBackgroundColor: string
    if (isInternal) {
      messageBackgroundColor = theme.tokens.background.semantic.warning.tonal.superSoft.default
    } else if (isCurrentUser) {
      messageBackgroundColor = theme.tokens.background.semantic.info.tonal.superSoft.default
    } else {
      messageBackgroundColor = theme.tokens.background.tonal.soft.default
    }
    return {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      backgroundColor: messageBackgroundColor,
      borderRadius: '24px',
      borderBottomLeftRadius: isCurrentUser ? '24px' : '0px',
      borderBottomRightRadius: isCurrentUser ? '0px' : '24px',
      width: 'fit-content',
      paddingLeft: isCurrentUser ? '16px' : '12px',
      paddingRight: '16px',
      paddingTop: '8px',
      paddingBottom: '12px',
      ...typography.body3,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        ...(isCurrentUser ? { right: -8 } : { left: -8, transform: 'scaleX(-1)' }),
        width: '8px',
        height: '16px',
        backgroundImage: `url("data:image/svg+xml,${speechBubbleTailSvg(messageBackgroundColor)}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '8px 16px',
        pointerEvents: 'none'
      }
    }
  }
)

export const StyledSpanCreatedAtDetails = styled('span')(({ theme }) => ({
  color: theme.tokens.foreground.main.secondary
}))

export const StyledSpanMessageAuthorName = styled('span')<{ $isCurrentUser: boolean; $isClickable?: boolean }>(
  ({ $isCurrentUser: isCurrentUser, theme, $isClickable: isClickable = false }) => ({
    fontWeight: isCurrentUser ? 'bold' : 'normal',
    color: isCurrentUser ? theme.tokens.foreground.main.primary : theme.tokens.foreground.main.link,
    ...(isClickable && {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline'
      }
    })
  })
)

export const StyledGridSystemEventMessageContent = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  backgroundColor: theme.tokens.background.main.default,
  borderRadius: '24px',
  border: `1px solid ${theme.tokens.border.main.tonal}`,
  padding: '8px 16px 12px 16px'
}))

// Copilot message layout (messageType: 'copilotmessage') – left-aligned speech bubble
export const StyledGridCopilotMessageCardContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  paddingLeft: '12px',
  paddingRight: '12px',
  paddingTop: '8px',
  paddingBottom: '12px'
}))

export const StyledGridCopilotMessageCardContent = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

const copilotThinkingSignatureTimelineStyles = (tokens: SemanticTokens) =>
  ({
    stepColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingLeft: '8px',
      paddingRight: '8px',
      height: 'fit-content'
    } satisfies CSSObject,
    stepNumber: {
      backgroundColor: tokens.background.brand.main.default,
      width: '22px',
      height: '22px',
      borderRadius: '9px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: tokens.foreground.main.staticWhite,
      '& .MuiTypography-root': {
        color: tokens.foreground.main.staticWhite
      }
    } satisfies CSSObject,
    connector: (visible: boolean): CSSObject => ({
      width: '4px',
      minHeight: '6px',
      backgroundColor: visible ? tokens.background.brand.main.default : 'transparent'
    })
  } as const)

export const StyledGridCopilotThinkingSignatureStepColumn = styled(Grid)(
  ({ theme }) => copilotThinkingSignatureTimelineStyles(theme.tokens).stepColumn
)

export const StyledGridCopilotThinkingSignatureStepNumber = styled(Grid)(
  ({ theme }) => copilotThinkingSignatureTimelineStyles(theme.tokens).stepNumber
)

export const StyledGridCopilotThinkingSignatureStepConnector = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})<{ isVisible: boolean }>(({ isVisible, theme }) => ({
  ...copilotThinkingSignatureTimelineStyles(theme.tokens).connector(isVisible),
  height: '100%'
}))

export const StyledGridCopilotThinkingSignatureDetailsConnectorColumn = styled(Grid)(() => ({
  display: 'flex',
  justifyContent: 'center',
  paddingLeft: '17px',
  paddingRight: '17px',
  alignSelf: 'stretch'
}))

export const StyledGridCopilotThinkingSignatureDetailsConnector = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})<{ isVisible: boolean }>(({ isVisible, theme }) => ({
  ...copilotThinkingSignatureTimelineStyles(theme.tokens).connector(isVisible),
  alignSelf: 'stretch'
}))

export const StyledGridCopilotThinkingSignatureCollapsedRow = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 0
}))

export const StyledGridCopilotThinkingSignatureExpandedRow = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 0,
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0
}))

export const StyledGridCopilotThinkingSignatureExpandedContent = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  paddingBottom: '12px',
  width: '100%',
  color: theme.tokens.foreground.main.secondary,
  '& .MuiTypography-root': {
    color: 'inherit',
    // Reasoning streaks concatenate segments with `\n`.
    whiteSpace: 'pre-wrap'
  }
}))

/**
 * Thought-only accordion: details shell already pads 8px (`spacing(1)`); summary pads
 * 16px (`spacing(3)`). Add another `spacing(1)` so body + shell = 16px and lines up
 * with the "Thought" label — not `spacing(2)` (12), which would total 20px.
 */
export const StyledGridCopilotThoughtOnlyExpandedContent = styled(StyledGridCopilotThinkingSignatureExpandedContent)(
  ({ theme }) => ({
    paddingLeft: theme.spacing(1),
    paddingBottom: 0
  })
)

/** Static search / inspect rows — no row surface (matches url_open; details area is transparent). */
export const StyledGridCopilotThinkingSignatureStaticRow = styled(Grid)(() => ({
  margin: 0,
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  minHeight: '28px',
  paddingRight: '8px',
  paddingLeft: '0px'
}))

/** Clickable url_open row: matches accordion summary row + trailing icon slot (gap like expand chevron) */
export const StyledAnchorCopilotThinkingSignatureUrlOpenRow = styled('a')(({ theme }) => ({
  margin: 0,
  width: 'fit-content',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: 0,
  minHeight: '28px',
  paddingRight: '8px',
  paddingLeft: '0px',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer'
}))

export const StyledAccordionCopilotThinkingSignatureRow = styled(Accordion)(({ theme }) => {
  const surface = theme.tokens.background.main.pressed

  return {
    margin: 0,
    backgroundColor: surface,
    '&.MuiPaper-root': {
      backgroundColor: surface,
      backgroundImage: 'none'
    },
    '&::before': {
      display: 'none'
    },
    '.MuiAccordionSummary-root': {
      minHeight: 'unset',
      height: '28px',
      paddingRight: '8px',
      paddingLeft: '0px',
      justifyContent: 'flex-start',
      gap: 0,
      backgroundColor: surface,
      '.MuiAccordionSummary-content': {
        flexGrow: 0
      },
      '.MuiAccordionSummary-expandIconWrapper': {
        '&:not(.Mui-expanded)': {
          paddingBottom: '4px'
        },
        '&.Mui-expanded': {
          paddingTop: '4px'
        }
      }
    },
    '.MuiAccordionDetails-root': {
      padding: 0,
      minWidth: 0,
      overflow: 'hidden',
      color: theme.tokens.foreground.main.secondary,
      backgroundColor: surface,
      '& .MuiTypography-root': {
        color: 'inherit'
      }
    }
  }
})

export const StyledAccordionCopilotThinkingSignatureContentsContainer = styled(Accordion)(({ theme }) => {
  const surface = theme.tokens.background.main.pressed
  const radius = theme.borderRadius.xSmall

  return {
    margin: 0,
    backgroundColor: surface,
    borderRadius: radius,
    overflow: 'hidden',
    '&.MuiPaper-root': {
      backgroundColor: surface,
      backgroundImage: 'none',
      borderRadius: radius,
      overflow: 'hidden'
    },
    '&::before': {
      display: 'none'
    },
    '.MuiAccordionSummary-root': {
      minHeight: 'unset',
      height: '34px',
      paddingRight: '8px',
      paddingLeft: '16px',
      backgroundColor: surface
    },
    '.MuiAccordionDetails-root': {
      paddingLeft: '8px',
      paddingRight: '16px',
      paddingTop: '4px',
      paddingBottom: '12px',
      backgroundColor: surface
    },
    '.MuiPaper-root-MuiAccordion-root': {
      margin: 0
    }
  }
})

export const StyledSpanCopilotMessageAuthorName = styled('span')(({ theme }) => ({
  fontWeight: 'normal',
  color: theme.tokens.foreground.main.primary
}))

const loaderAnimation = keyframes({
  to: {
    clipPath: 'inset(0 -34% 0 0)'
  }
})

export const StyledGridCopilotThinkingActionCard = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  width: '100%',
  // Flex default min-width:auto expands the card to long chip content; allow shrink.
  minWidth: 0,
  overflow: 'hidden',
  padding: '8px',
  borderRadius: '16px',
  color: theme.tokens.foreground.main.primary,
  backgroundColor: theme.tokens.background.main.default,
  border: `1px solid ${theme.tokens.border.main.tonal}`,
  boxSizing: 'border-box'
}))

export const StyledGridCopilotActionCardContent = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  alignContent: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden'
}))

export const StyledGridCopilotActionCardHighlightAction = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  button: {
    alignSelf: 'center'
  }
}))

export const StyledGridCopilotThinkingActionCardsStack = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  minWidth: 0
}))

export const StyledLoadingDots = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: '10px',
  aspectRatio: '4',
  background: `radial-gradient(circle closest-side, ${theme.tokens.foreground.main.primary} 90%, transparent) 0/calc(100%/3) 100% space`,
  clipPath: 'inset(0 100% 0 0)',
  animation: `${loaderAnimation} 1.8s steps(4) infinite`,
  marginLeft: '2px',
  verticalAlign: 'baseline',
  marginBottom: '0px'
}))
